import type { PaletteMode } from '@mui/material/styles'

export type BadgeTone =
  | 'neutral'
  | 'green'
  | 'warn'
  | 'danger'
  | 'blue'
  | 'violet'
  | 'required'

export type BadgeToneColors = {
  bg: string
  fg: string
}

export const badgeTokensLight: Record<BadgeTone, BadgeToneColors> = {
  neutral: { bg: '#EEF1F4', fg: '#5A6473' },
  green: { bg: '#DCF1EF', fg: '#0FA89F' },
  warn: { bg: '#FDEBD9', fg: '#E87A1A' },
  danger: { bg: '#FDDDDD', fg: '#E63B3B' },
  blue: { bg: '#E5ECF1', fg: '#124C69' },
  violet: { bg: '#ECE5F1', fg: '#5C3E7F' },
  required: { bg: '#FCE6E6', fg: '#C9302C' },
}

export const badgeTokensDark: Record<BadgeTone, BadgeToneColors> = {
  neutral: { bg: 'rgba(255, 255, 255, 0.08)', fg: '#A0AEC0' },
  green: { bg: 'rgba(15, 168, 159, 0.18)', fg: '#5EEAD4' },
  warn: { bg: 'rgba(232, 122, 26, 0.32)', fg: '#FB923C' },
  danger: { bg: 'rgba(230, 59, 59, 0.18)', fg: '#F87171' },
  blue: { bg: 'rgba(125, 211, 252, 0.12)', fg: '#93C5FD' },
  violet: { bg: 'rgba(158, 119, 237, 0.16)', fg: '#D6BBFB' },
  required: { bg: 'rgba(201, 48, 44, 0.18)', fg: '#FCA5A5' },
}

/** @deprecated Prefer getBadgeTokens(mode) for theme-aware usage. */
export const badgeTokens = badgeTokensLight

export const getBadgeTokens = (
  mode: PaletteMode = 'light'
): Record<BadgeTone, BadgeToneColors> =>
  mode === 'dark' ? badgeTokensDark : badgeTokensLight

/** Fond hover : léger mix du fg dans le bg, sans nouvelle couleur. */
export const badgeHoverBg = (
  tone: BadgeTone,
  mode: PaletteMode = 'light'
): string => {
  const tokens = getBadgeTokens(mode)
  return `color-mix(in srgb, ${tokens[tone].fg} 8%, ${tokens[tone].bg})`
}

export const badgeActiveBg = (
  tone: BadgeTone,
  mode: PaletteMode = 'light'
): string => {
  const tokens = getBadgeTokens(mode)
  return `color-mix(in srgb, ${tokens[tone].fg} 14%, ${tokens[tone].bg})`
}

const tokensToCssVariables = (
  tokens: Record<BadgeTone, BadgeToneColors>
): Record<string, string> =>
  Object.entries(tokens).reduce(
    (vars, [tone, colors]) => {
      vars[`--color-badge-${tone}-bg`] = colors.bg
      vars[`--color-badge-${tone}-fg`] = colors.fg
      return vars
    },
    {} as Record<string, string>
  )

export const badgeCssVariables = tokensToCssVariables(badgeTokensLight)

export const badgeCssVariablesDark = tokensToCssVariables(badgeTokensDark)
